import axios from "axios";
import { getToken, saveToken, getUserCacheSync } from "@/lib/authStorage";
import { updateSavedAccountToken } from "@/lib/authAccounts";
import {
  getRefreshPromise,
  setRefreshPromise,
  resetAuthRefreshState,
} from "@/lib/authRefreshState";

export { resetAuthRefreshState };

/** Toujours same-origin pour que les cookies HttpOnly survivent (www / sans-www). */
export function getApiBase() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || "";
}

const api = axios.create({
  baseURL: `${getApiBase()}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

function isAuthFailure(error) {
  return error?.response?.status === 401 || error?.response?.status === 403;
}

export async function refreshSession() {
  if (!getRefreshPromise()) {
    setRefreshPromise(
      axios
        .post(
          `${getApiBase()}/api/auth/refresh`,
          { token: getToken() || undefined },
          { withCredentials: true, timeout: 20000 }
        )
        .then((res) => {
          if (res.data?.token) {
            saveToken(res.data.token);
            syncTokenToSavedAccount(res.data.token);
          }
          return res.data;
        })
        .finally(() => {
          setRefreshPromise(null);
        })
    );
  }
  return getRefreshPromise();
}

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function syncTokenToSavedAccount(token) {
  if (!token) return;
  const user = getUserCacheSync();
  if (user?.user_id) {
    updateSavedAccountToken(user.user_id, token);
  }
}

api.interceptors.response.use(
  (res) => {
    if (res.data?.token) {
      saveToken(res.data.token);
      syncTokenToSavedAccount(res.data.token);
    }
    return res;
  },
  async (error) => {
    const original = error.config;
    if (!original || original._retry) return Promise.reject(error);

    if (
      isAuthFailure(error) &&
      !original.url?.includes("/auth/login") &&
      !original.url?.includes("/auth/register") &&
      !original.url?.includes("/auth/refresh")
    ) {
      original._retry = true;
      try {
        await refreshSession();
        const token = getToken();
        if (token) original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch {
        /* AuthContext gère la déconnexion — ne pas effacer le token ici (race mobile) */
      }
    }
    return Promise.reject(error);
  }
);

export default api;